<template>
  <div id="app">

    <video class="app__video" controls playsinline preload="none" ref="video"
      @error="videoError = true" @loadeddata="videoError = false" @webkitendfullscreen="onVideoClose">
      <source src="/static/tutorial.mp4" type="video/mp4" @error="videoError = true">
    </video>
    <navbar-top @menu="isMenuOpened = !isMenuOpened" @close="isMenuOpened = false" :isMenuOpened="isMenuOpened" @video="onVideoPlay"/>

    <div class="content-wrapper container-fluid">
      <transition name="slide" mode="out-in" v-if="!isCollapsedMenu">
        <navbar-left v-if="!isLeftBarHidden" @video="onVideoPlay" :isMenuOpened="isMenuOpened" @close="isMenuOpened = false"/>
      </transition>
      <navbar-left v-else-if="!isLeftBarHidden && isCollapsedMenu" @video="onVideoPlay" :isMenuOpened="isMenuOpened" @close="isMenuOpened = false"/>
      <div class="content">
        <div v-if="userLoadFailed && getLoadUserError && getLoadUserError.network && $route.name !== 'AuthLogin'" role="alert" class="recovery-notice">
          Не удалось загрузить профиль.
          <button type="button" @click="loadUser">Повторить</button>
        </div>
        <div v-if="videoError" role="alert" class="recovery-notice">
          Не удалось открыть видео.
          <button type="button" @click="onVideoPlay">Повторить</button>
        </div>
        <transition name="zoom-fade" mode="out-in">
          <router-view class="content-router"/>
        </transition>

        <transition name="fade" mode="out-in">
          <ReceipesDetail class="content-recipe" v-if="showRecipe" @close="onRecipeClose" />
        </transition>
      </div>
    </div>
    <my-footer v-if="!showRecipe"/>
  </div>
</template>

<script>
  import NavbarLeft from "./components/TheNavbarLeft";
  import NavbarTop from "./components/TheNavbarTop";
  import { mapActions, mapGetters } from 'vuex';
  import {LOADING_STATUS} from "./constants";
  import MyFooter from './components/Footer';
  import { postLoginRoute } from './utils/session-loader';
  import UserApi from './api/UserApi';
  import ReceipesDetail from './views/ReceipesDetail.vue';
  export default {
    name: "App",
    components: {
      NavbarTop,
      NavbarLeft,
      MyFooter,
      ReceipesDetail,
    },
    data: () => ({
      recipeInfo: {},
      videoError: false,
      videoRequestId: 0,
      showRecipe: false,

      isMenuOpened: false,
    }),
    computed: {
      ...mapGetters([
        'getLoadUserError',
        'getUser',
        'getUserFamily',
        'getUserLoadingStatus',
        'getCurrentProfile'
      ]),
      userLoadFailed() { return this.getUserLoadingStatus === LOADING_STATUS.FAIL; },
      isLeftBarHidden() {
        return (
          this.$route.meta.hideLeftBar ||
          !this.getUserFamily.find(el => el.user_id === this.getUser?.id)
        )
      },
      isCollapsedMenu() {
        return this.$mq === 'xs' || this.$mq === 'sm';
      },
    },
    mounted() {
      document.addEventListener('fullscreenchange', this.onFullscreenChange);
      document.addEventListener('webkitfullscreenchange', this.onFullscreenChange);
    },
    beforeDestroy() {
      document.removeEventListener('fullscreenchange', this.onFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', this.onFullscreenChange);
    },
    async created() {
      const loadUserPromise = this.loadUser();
      const routerReadyPromise = new Promise(resolve => this.$router.onReady(resolve));
      UserApi.prefetch();

      this.$root.$on('SHOW_RECIPE', async (recipe) => {
        this.$router.push({
          params: { ...this.$route.params },
          query: { ...recipe, },
        })
      })

      await Promise.all([loadUserPromise, routerReadyPromise]);
      this.settleSessionRoute();
    },
    watch: {
      getUserLoadingStatus(value) { if (value !== LOADING_STATUS.INITIAL) this.settleSessionRoute(); },
      '$route.fullPath'() { this.settleSessionRoute(); },
      '$route.name'() { this.isMenuOpened = false; },
      '$route.query': {
        immediate: true,
        handler(value) {
          if (value.recipe_id) {
            document.body.classList.add("noscroll");
            this.showRecipe = true;
          } else {
            document.body.classList.remove("noscroll");
            this.showRecipe = false;
          }
        }
      }
    },
    methods: {
      ...mapActions([
        'loadUser'
      ]),
      settleSessionRoute() {
        let target;
        if (this.getUserLoadingStatus === LOADING_STATUS.SUCCESS && this.getUser) {
          if (this.$route.name === 'AuthLogin') {
            target = postLoginRoute(this.$router, this.getCurrentProfile, this.$route.query.redirect);
          } else if (!this.getCurrentProfile.id && this.$route.name !== 'Registration' && !this.$route.path.startsWith('/onboarding')) {
            target = { path: '/onboarding' };
          }
        } else if (((this.getLoadUserError && this.getLoadUserError.auth_required) || (!this.getUser && this.getUserLoadingStatus === LOADING_STATUS.INITIAL)) && this.$route.matched.some(route => route.meta.requiresAuth)) {
          target = { name: 'AuthLogin', query: { redirect: this.$route.fullPath } };
        }
        if (target) this.$router.replace(target).catch(error => {
          if (!['NavigationDuplicated', 'NavigationRedirected', 'NavigationCancelled'].includes(error.name)) console.error(error);
        });
      },
      onRecipeClose() {
        document.body.classList.remove("noscroll");
        this.showRecipe = false
        this.$router.push({ query: {} });
      },
      async onVideoPlay() {
        const requestId = ++this.videoRequestId;
        const video = this.$refs.video;
        const retry = this.videoError || !!video.error;
        this.videoError = false;
        if (retry) video.load();
        try {
          const fullscreen = video.requestFullscreen || video.webkitRequestFullscreen || video.webkitEnterFullscreen;
          if (!fullscreen) { window.open(video.currentSrc || '/static/tutorial.mp4', '_blank'); return; }
          await fullscreen.call(video);
          if (requestId !== this.videoRequestId) return;
          await video.play();
        } catch (_) {
          if (requestId === this.videoRequestId) this.videoError = true;
        }
      },
      onFullscreenChange() {
        if (!document.fullscreenElement && !document.webkitFullscreenElement) this.onVideoClose();
      },
      onVideoClose() {
        ++this.videoRequestId;
        this.$refs.video.pause();
      }
    }
  }
</script>

<style lang="scss" scoped>
.k-subscribe__loading-wrapper {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: #fff;
  opacity: 0.85;
  display: flex;
  justify-content: center;
  align-content: center;
  z-index: 3;
}
.content {
  position: relative;
}
.content-recipe {
  position: fixed;
  top: 78px;
  left: 78px;
  width: calc(100vw - 78px);
  height: calc(100vh - 63px);
  overflow: scroll;
  z-index: 9;
}

.content-router {
  z-index: 8;
  position: relative;
}

.app__video { position: fixed; top: 0; left: -10000px; }
.app__video:fullscreen { left: 0; top: 0; width: 100%; height: 100%; }
.app__video:-webkit-full-screen { left: 0; top: 0; width: 100%; height: 100%; }

@media (max-width: 767.98px) {
  .content-recipe {
    top: 60px;
    left: 0;
    width: 100vw;
    height: calc(100vh - 60px);
  }
}
</style>

<style>
.recovery-notice { margin: 24px 12px; color: #77767e; text-align: center; font-size: 15px; }
.recovery-notice button { margin-left: 8px; padding: 0; border: 0; background: transparent; color: #f68c1e; font: inherit; cursor: pointer; text-decoration: underline; }
.noscroll {
  height: 100vh;
  overflow: hidden;
}
/* ZOOM FADE */
.zoom-fade-enter-active,
.zoom-fade-leave-active {
  transition: transform .1s, opacity .1s ease-in-out;
}
.zoom-fade-enter {
  transform: scale(0.97);
  opacity: 0;
}

.zoom-fade-leave-to {
  transform: scale(1.03);
  opacity: 0;
}

.slide-fade-enter-active, .slide-fade-leave-active {
  transition: opacity .25s, transform .3s;
}
.slide-fade-enter {
  opacity: 0;
  transform: translateX(-30%);
}

.slide-fade-leave-to{
  opacity: 0;
  transform: translateX(30%);
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity .12s ease-in-out;
}

.fade-enter,
.fade-leave-to {
    opacity: 0;
}

.slide-leave-active,
.slide-enter-active {
  transition: .5s;
}
.slide-enter {
  transform: translate(-100%, 0);
}
.slide-leave-to {
  transform: translate(-100%, 0);
}

.content-wrapper {
  min-height: 81.6vh;
  position: relative;
}

</style>
